import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, RefreshCw, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 1 — Promoter Management (Product Manager / Admin desktop view).
 *
 * Lists all promoters (server-side filtered for sales_manager) with a search box,
 * status filter, and quick activate/deactivate. The "Onboard new" CTA jumps to the
 * /promoter/manage/new form.
 */
export default function PromoterManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active"); // active | inactive | all
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promoter/promoters", { params: { status, q: q || undefined } });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load promoters", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/promoter/promoters/${row.id}/active`, { active: !row.active });
      toast.success(row.active ? "Promoter deactivated" : "Promoter activated");
      load();
    } catch (e) {
      toast.error("Could not change status", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-4" data-testid="promoter-management">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Promoter Management</h1>
          <p className="text-sm text-slate-500 mt-1">Onboard promoters, manage profiles, and toggle login access.</p>
        </div>
        <Button onClick={() => navigate("/promoter/manage/new")}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="onboard-promoter-btn">
          <Plus size={16} className="mr-1" /> Onboard new promoter
        </Button>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <form onSubmit={onSearch} className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search by name, phone, ID, dealer, area..."
                     className="pl-9" data-testid="promoter-search-input" />
            </div>
            <div className="flex gap-1" data-testid="promoter-status-tabs">
              {["active", "inactive", "all"].map((s) => (
                <button key={s} type="button"
                        onClick={() => setStatus(s)}
                        className={`text-xs px-3 py-1.5 rounded-md border ${
                          status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid={`status-tab-${s}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <Button type="submit" variant="outline" data-testid="promoter-search-btn">
              <Search size={14} className="mr-1" /> Search
            </Button>
            <Button type="button" variant="outline" onClick={load} data-testid="promoter-refresh-btn">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promoter-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Promoter ID</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Dealer / Counter</th>
                  <th className="text-left px-3 py-2">Area</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-8" data-testid="promoter-table-loading">Loading...</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-8" data-testid="promoter-table-empty">
                    No promoters yet — click &quot;Onboard new promoter&quot; to add the first one.
                  </td></tr>
                )}
                {!loading && rows.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50"
                      data-testid={`promoter-row-${p.promoter_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.promoter_id}</td>
                    <td className="px-3 py-2">
                      <Link to={`/promoter/manage/${p.id}`} className="text-slate-900 font-medium hover:underline"
                            data-testid={`promoter-name-link-${p.promoter_id}`}>{p.name}</Link>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{p.phone}</td>
                    <td className="px-3 py-2 text-slate-600">{p.assigned_dealer_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{p.area || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {p.promoter_type === "salaried" ? "Salaried" : "Incentive"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>
                        {p.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => toggleActive(p)}
                              data-testid={`toggle-active-${p.promoter_id}`}>
                        {p.active ? (<><UserX size={12} className="mr-1" /> Deactivate</>)
                                  : (<><UserCheck size={12} className="mr-1" /> Activate</>)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
